"use client";

import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Empty, Flex, Skeleton } from "antd/lib";
import FadeIn from "@/src/components/Common/FadeIn/FadeIn";
import styles from "@/src/components/Events/Events.module.scss";
import { Event, Report } from "@/types/events";
import { SearchParams as SearchParamsType } from "@/types/common";
import { useSearchParams } from "next/navigation";
import { EventsTimePeriods } from "@/constants/events";
import { useEventsFetchers } from "@/hooks/useEventsFetchers";
import EventCard from "@/src/components/Events/EventCard";
import { useAuth } from "@/context/AuthContext";
import { SearchParams } from "@/constants/common";
import { v4 as uuidv4 } from "uuid";

interface EventsListProps {
  events?: (Event | Report)[];
  isMyEvents?: boolean;
  isInReportingContext?: boolean;
  firstEventRef?: React.RefObject<HTMLAnchorElement | null>;
  timePeriod?: EventsTimePeriods;
  activityTypeId?: string;
  eventsLimit?: number;
  withPrograms?: boolean;
  searchParamsWillBeAdded?: boolean;
  filtersOptionsError?: boolean;
}

const EventsList: React.FC<EventsListProps> = ({
  events,
  isMyEvents,
  isInReportingContext,
  firstEventRef,
  timePeriod,
  activityTypeId,
  eventsLimit = 20,
  withPrograms = false,
  searchParamsWillBeAdded = false,
  filtersOptionsError = false,
}) => {
  const { t } = useTranslation();
  const searchParams = useSearchParams();
  const { fetchEvents, fetchNextEventsPageCount } = useEventsFetchers();
  const { meDataError } = useAuth();

  const [eventsLoading, setEventsLoading] = useState(!events?.length);
  const [nextPageCountLoading, setNextPageCountLoading] = useState(!events?.length);
  const [currentEvents, setCurrentEvents] = useState<(Event | Report)[]>(events || []);
  const [newEvents, setNewEvents] = useState<(Event | Report)[]>([]);
  const [eventsOffset, setEventsOffset] = useState(0);
  const [eventsToLoadCount, setEventsToLoadCount] = useState<number>();

  const abortController = new AbortController();
  const reported = searchParams.get(SearchParams.filterReported);

  const eventsAreReported = reported ? parseInt(reported) : false;

  const fetchEventsListWithPaginationData = async (
    newEventsToSet?: Event[],
    eventsOffsetToSet?: number,
    eventsToLoadCountToSet?: number,
  ) => {
    const filters: SearchParamsType = {};
    const eventsToLoad = eventsToLoadCount || eventsToLoadCountToSet;

    if (searchParams) {
      for (const [key, value] of searchParams.entries()) {
        filters[key] = value;
      }
    }
    if (eventsToLoad !== undefined && eventsToLoad < eventsLimit) {
      setEventsToLoadCount(0);
      await fetchEventsList(filters, newEventsToSet, eventsOffsetToSet);
    } else {
      await Promise.all([
        fetchEventsList(filters, newEventsToSet, eventsOffsetToSet),
        fetchNextPageCount(filters, eventsOffsetToSet),
      ]);
    }
  };

  const fetchEventsList = async (
    filters: SearchParamsType,
    newEventsToSet?: (Event | Report)[],
    eventsOffsetToSet?: number,
  ) => {
    if (!eventsLoading) setEventsLoading(true);
    setCurrentEvents(newEventsToSet || [...currentEvents, ...newEvents]);

    const response = await fetchEvents(
      eventsOffsetToSet !== undefined ? eventsOffsetToSet : eventsOffset,
      eventsLimit,
      filters,
      setEventsLoading,
      abortController.signal,
      timePeriod,
      activityTypeId,
    );

    if (!response) return;

    const { data } = response;

    setNewEvents(data);
    setEventsOffset((prevEventsOffset) => prevEventsOffset + data.length);

    setEventsLoading(false);
  };

  const fetchNextPageCount = async (filters: SearchParamsType, eventsOffsetToSet?: number) => {
    if (!nextPageCountLoading) setNextPageCountLoading(true);

    const response = await fetchNextEventsPageCount(
      eventsOffsetToSet !== undefined ? eventsOffsetToSet : eventsOffset,
      eventsLimit,
      filters,
      setNextPageCountLoading,
      abortController.signal,
      timePeriod,
      activityTypeId,
    );

    if (!response) return;

    const { next_page_count } = response;

    setEventsToLoadCount(next_page_count);
    setNextPageCountLoading(false);
  };

  useEffect(() => {
    if (eventsToLoadCount !== undefined) setEventsToLoadCount(undefined);
    if (newEvents.length) setNewEvents([]);
    if (eventsOffset) setEventsOffset(0);

    if ((isInReportingContext && !activityTypeId) || !!events?.length || searchParamsWillBeAdded) return;

    fetchEventsListWithPaginationData([], 0, undefined);

    return () => {
      abortController.abort();
    };
  }, [searchParams, searchParamsWillBeAdded]);

  useEffect(() => {
    if (!meDataError && !filtersOptionsError) return;

    setEventsLoading(false);
  }, [meDataError, filtersOptionsError]);

  return (
    <Flex vertical flex={1} gap={32}>
      {eventsLoading || currentEvents.length || newEvents.length ? (
        <>
          {currentEvents.map((event) => (
            <EventCard
              key={"uuid" in event ? event.uuid : "event_uuid" in event ? event.event_uuid || uuidv4() : uuidv4()}
              event={eventsAreReported ? undefined : (event as Event)}
              report={eventsAreReported ? (event as Report) : undefined}
              isMyEvents={isMyEvents}
              isInReportingContext={isInReportingContext}
              materialsListButton={withPrograms}
            />
          ))}

          {!events?.length && (
            <>
              <Skeleton active loading={eventsLoading}>
                <FadeIn>
                  <Flex vertical className={styles.container} gap={32}>
                    {newEvents.map((event, i) => (
                      <EventCard
                        key={
                          "uuid" in event ? event.uuid : "event_uuid" in event ? event.event_uuid || uuidv4() : uuidv4()
                        }
                        event={eventsAreReported ? undefined : (event as Event)}
                        report={eventsAreReported ? (event as Report) : undefined}
                        isMyEvents={isMyEvents}
                        isInReportingContext={isInReportingContext}
                        materialsListButton={withPrograms}
                        detailsBtnRef={i === 0 ? firstEventRef : undefined}
                      />
                    ))}
                  </Flex>
                </FadeIn>
              </Skeleton>
              {!!(eventsLoading || nextPageCountLoading || eventsToLoadCount) && (
                <Skeleton active loading={eventsLoading || nextPageCountLoading}>
                  <FadeIn>
                    {!!eventsToLoadCount && (
                      <p className={styles.loadMore} onClick={() => fetchEventsListWithPaginationData()}>
                        {t("eventsPage.buttons.loadMore")} ({eventsToLoadCount})
                      </p>
                    )}
                  </FadeIn>
                </Skeleton>
              )}
            </>
          )}
        </>
      ) : (
        <Empty />
      )}
    </Flex>
  );
};

export default EventsList;
